import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Heading,
  Card,
  CardBody,
  Button,
  Icon,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  UnorderedList,
  ListItem,
  Image,
  useColorModeValue,
  SimpleGrid,
  Checkbox
} from '@chakra-ui/react';
import {
  Play,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Star,
  Zap,
  Shield,
  TrendingUp,
  Target,
  BookOpen,
  Award,
  ArrowRight
} from 'lucide-react';

interface OnboardingTutorialProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({ 
  isOpen, 
  onClose, 
  onComplete 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const bgColor = useColorModeValue('white', 'gray.800');
  
  const steps = [
    {
      id: 0,
      title: '👋 Welcome to Alpha Playbook',
      type: 'welcome',
      content: {
        heading: 'Your Gateway to RWA Investing',
        description: 'Real-World Assets (RWAs) represent the future of finance - traditional assets like Treasury bills, bonds, and credit products tokenized on blockchain.',
        highlights: [
          '🏆 $24B+ market size growing +14% monthly',
          '🛡️ Government-backed securities starting at $100',
          '📊 Professional-grade analysis tools',
          '🤖 AI-powered trading signals'
        ],
        marketLeaders: [
          { name: 'Ondo Finance', tvl: '$1.5B+', focus: 'Tokenized Treasuries' },
          { name: 'BlackRock BUIDL', tvl: '$2.9B', focus: 'Treasury Fund' },
          { name: 'Centrifuge', tvl: '$200M+', focus: 'Real-World Credit' }
        ]
      }
    },
    {
      id: 1,
      title: '🎯 Platform Overview',
      type: 'navigation',
      content: {
        description: 'Alpha Playbook has 5 main sections. Each serves a specific purpose in your investment journey.',
        tabs: [
          {
            name: 'Discovery',
            icon: '🔍',
            purpose: 'Find trending crypto opportunities',
            whenToUse: 'Daily market scanning',
            beginner: 'Start here to explore markets'
          },
          {
            name: 'Analytics',
            icon: '📊',
            purpose: 'Deep technical analysis',
            whenToUse: 'Before making trades',
            beginner: 'Use after Discovery'
          },
          {
            name: 'RWA Playbook',
            icon: '💎',
            purpose: 'RWA signals and backtesting',
            whenToUse: 'For RWA-specific strategies',
            beginner: 'Your main workspace for RWAs'
          },
          {
            name: 'CashFlow Beast',
            icon: '🔥',
            purpose: 'Automated trading',
            whenToUse: 'When ready for automation',
            beginner: 'Advanced - use after manual experience'
          }
        ],
        recommendation: 'Beginners should focus on RWA Playbook first'
      }
    },
    {
      id: 2,
      title: '📈 Understanding Signals',
      type: 'education',
      content: {
        description: 'Trading signals are AI-generated recommendations based on market analysis.',
        signalTypes: [
          {
            type: 'BUY',
            color: 'green',
            meaning: 'Algorithm suggests price may increase',
            action: 'Consider opening a position',
            confidence: 'Look for >85% confidence'
          },
          {
            type: 'HOLD',
            color: 'yellow',
            meaning: 'Maintain current positions',
            action: 'No immediate action needed',
            confidence: 'Usually medium confidence'
          },
          {
            type: 'SELL',
            color: 'red',
            meaning: 'Algorithm suggests taking profits',
            action: 'Consider closing positions',
            confidence: 'High confidence sells are priority'
          }
        ],
        confidenceGuide: {
          'High (>85%)': {
            meaning: 'Strong algorithmic conviction',
            reliability: 'Most reliable signals',
            action: 'Safest to follow'
          },
          'Medium (70-85%)': {
            meaning: 'Moderate conviction',
            reliability: 'Decent reliability',
            action: 'Combine with other analysis'
          },
          'Low (<70%)': {
            meaning: 'Weak signal',
            reliability: 'Less reliable',
            action: 'Wait for better opportunities'
          }
        }
      }
    },
    {
      id: 3,
      title: '🧪 Your First Backtest',
      type: 'hands-on',
      content: {
        description: 'Backtesting shows how a strategy would have performed historically. It\'s essential for validating strategies before live trading.',
        walkthrough: [
          {
            step: 'Navigate to RWA Playbook',
            action: 'Click the RWA Playbook tab',
            expected: 'See RWA trading signals'
          },
          {
            step: 'Find a High Confidence Signal',
            action: 'Look for signals with green "HIGH" badges',
            expected: 'Signals with >85% confidence'
          },
          {
            step: 'Click "Run Backtest"',
            action: 'Click the backtest button on any signal',
            expected: 'Backtest modal opens'
          },
          {
            step: 'Configure Your Test',
            action: 'Select ONDO, CFG, VBILL as starter assets',
            expected: 'Asset selection updated'
          },
          {
            step: 'Choose Strategy',
            action: 'Select "SMA Momentum" (safest for beginners)',
            expected: 'Strategy parameters appear'
          },
          {
            step: 'Run the Test',
            action: 'Click "Run Advanced RWA Backtest"',
            expected: 'Results with charts and metrics'
          }
        ],
        keyMetrics: [
          {
            metric: 'Total Return',
            good: '>10% annually',
            meaning: 'Profit percentage over time period'
          },
          {
            metric: 'Sharpe Ratio',
            good: '>1.0',
            meaning: 'Risk-adjusted returns (higher is better)'
          },
          {
            metric: 'Max Drawdown',
            good: '<20%',
            meaning: 'Largest peak-to-trough loss'
          },
          {
            metric: 'Win Rate',
            good: '>60%',
            meaning: 'Percentage of profitable trades'
          }
        ]
      }
    },
    {
      id: 4,
      title: '🛡️ Risk Management',
      type: 'safety',
      content: {
        description: 'Successful RWA investing requires understanding and managing risks.',
        riskTypes: [
          {
            risk: 'Market Risk',
            description: 'Asset prices can go down',
            mitigation: 'Diversify across multiple RWA types',
            level: 'Medium'
          },
          {
            risk: 'Smart Contract Risk',
            description: 'Technical vulnerabilities in protocols',
            mitigation: 'Stick to established protocols with audits',
            level: 'Low-Medium'
          },
          {
            risk: 'Regulatory Risk',
            description: 'Changing regulations could affect tokenized assets',
            mitigation: 'Monitor regulatory developments',
            level: 'Medium'
          },
          {
            risk: 'Liquidity Risk',
            description: 'Difficulty selling when needed',
            mitigation: 'Check daily trading volumes before buying',
            level: 'Low-Medium'
          }
        ],
        bestPractices: [
          'Never invest more than you can afford to lose',
          'Start with small amounts ($100-$500)',
          'Diversify across 3-5 different RWA assets',
          'Focus on high-confidence signals (>85%)',
          'Use backtesting before live trading',
          'Set stop-losses to limit downside',
          'Review positions monthly, not daily'
        ],
        beginnerAllocation: {
          'VBILL (Low Risk)': '40%',
          'ONDO (Medium Risk)': '30%',
          'ETH (Infrastructure)': '20%',
          'CFG (Higher Risk)': '10%'
        }
      }
    },
    {
      id: 5,
      title: '🚀 Ready to Start!',
      type: 'completion',
      content: {
        description: 'You\'re now equipped with the fundamentals of RWA investing using Alpha Playbook!',
        nextSteps: [
          {
            action: 'Complete Your First Analysis',
            details: 'Go to RWA Playbook and analyze 2-3 signals',
            timeEstimate: '15 minutes'
          },
          {
            action: 'Run Practice Backtests',
            details: 'Test different strategies with historical data',
            timeEstimate: '20 minutes'
          },
          {
            action: 'Read the Knowledge Base',
            details: 'Dive deeper into specific topics',
            timeEstimate: '30 minutes'
          },
          {
            action: 'Start Small',
            details: 'Begin with $100-200 in VBILL or ONDO',
            timeEstimate: 'When ready'
          }
        ],
        resources: [
          'Knowledge Base (available in top menu)',
          'Troubleshooting guide for common issues',
          'RWA market data and insights',
          'Community support channels'
        ],
        reminder: 'Remember: This is educational content. Always do your own research and never invest more than you can afford to lose.'
      }
    }
  ];

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const markStepCompleted = (stepId: number) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const handleNext = () => {
    markStepCompleted(currentStep);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepId: number) => {
    setCurrentStep(stepId);
  };

  const renderStepContent = () => {
    const step = currentStepData;
    
    switch (step.type) {
      case 'welcome':
        return (
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Heading size="lg" mb={4} color="purple.600">
                {step.content.heading}
              </Heading>
              <Text fontSize="lg" mb={6} color="gray.600">
                {step.content.description}
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {(step.content.highlights || []).map((highlight: string, idx: number) => (
                <Box key={idx} p={4} bg="purple.50" borderRadius="lg" borderLeft="4px solid" borderColor="purple.400">
                  <Text color="purple.800">{highlight}</Text>
                </Box>
              ))}
            </SimpleGrid>

            <Box>
              <Text fontWeight="semibold" mb={3} color="gray.700">Current Market Leaders:</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                {(step.content.marketLeaders || []).map((leader: any, idx: number) => (
                  <Box key={idx} p={3} bg="green.50" borderRadius="lg" textAlign="center">
                    <Text fontWeight="bold" color="green.800">{leader.name}</Text>
                    <Text fontSize="sm" color="green.600">{leader.tvl}</Text>
                    <Text fontSize="xs" color="green.500">{leader.focus}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        );

      case 'navigation':
        return (
          <VStack spacing={6} align="stretch">
            <Text mb={4}>{step.content.description}</Text>
            
            <VStack spacing={3}>
              {(step.content.tabs || []).map((tab: any, idx: number) => (
                <Box key={idx} p={4} bg="blue.50" borderRadius="lg" w="full">
                  <HStack spacing={3} mb={2}>
                    <Text fontSize="2xl">{tab.icon}</Text>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold" color="blue.800">{tab.name}</Text>
                      <Text fontSize="sm" color="blue.600">{tab.purpose}</Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={4} fontSize="sm">
                    <Text color="blue.700"><strong>When to use:</strong> {tab.whenToUse}</Text>
                  </HStack>
                  <Box mt={2} p={2} bg="blue.100" borderRadius="md">
                    <Text fontSize="sm" color="blue.800">
                      <strong>For beginners:</strong> {tab.beginner}
                    </Text>
                  </Box>
                </Box>
              ))}
            </VStack>

            <Alert status="info" variant="subtle">
              <AlertIcon />
              <AlertTitle>Recommendation:</AlertTitle>
              <AlertDescription>{step.content.recommendation}</AlertDescription>
            </Alert>
          </VStack>
        );

      case 'education':
        return (
          <VStack spacing={6} align="stretch">
            <Text mb={4}>{step.content.description}</Text>
            
            <Box>
              <Text fontWeight="semibold" mb={3}>Signal Types:</Text>
              <VStack spacing={3}>
                {step.content.signalTypes.map((signal: any, idx: number) => (
                  <Box key={idx} p={4} bg={`${signal.color}.50`} borderRadius="lg" w="full" 
                       borderLeft="4px solid" borderColor={`${signal.color}.400`}>
                    <HStack justify="space-between" mb={2}>
                      <Badge colorScheme={signal.color} variant="solid" size="lg">
                        {signal.type}
                      </Badge>
                      <Text fontSize="sm" color={`${signal.color}.600`}>
                        {signal.confidence}
                      </Text>
                    </HStack>
                    <Text color={`${signal.color}.800`} mb={1}>
                      <strong>Meaning:</strong> {signal.meaning}
                    </Text>
                    <Text color={`${signal.color}.700`}>
                      <strong>Action:</strong> {signal.action}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={3}>Confidence Levels:</Text>
              <VStack spacing={2}>
                {Object.entries(step.content.confidenceGuide).map(([level, guide]: [string, any], idx: number) => (
                  <Box key={idx} p={3} bg="gray.50" borderRadius="lg" w="full">
                    <Text fontWeight="semibold" color="gray.800">{level}</Text>
                    <Text fontSize="sm" color="gray.600" mt={1}>{guide.meaning} - {guide.action}</Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          </VStack>
        );

      case 'hands-on':
        return (
          <VStack spacing={6} align="stretch">
            <Text mb={4}>{step.content.description}</Text>
            
            <Box>
              <Text fontWeight="semibold" mb={3}>Step-by-Step Walkthrough:</Text>
              <VStack spacing={3}>
                {step.content.walkthrough.map((item: any, idx: number) => (
                  <Box key={idx} p={4} bg="orange.50" borderRadius="lg" w="full">
                    <Text fontWeight="semibold" color="orange.800" mb={1}>
                      {idx + 1}. {item.step}
                    </Text>
                    <Text color="orange.700" mb={2}>
                      <strong>Action:</strong> {item.action}
                    </Text>
                    <Text color="orange.600" fontSize="sm">
                      <strong>Expected:</strong> {item.expected}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={3}>Key Metrics to Watch:</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {step.content.keyMetrics.map((metric: any, idx: number) => (
                  <Box key={idx} p={3} bg="teal.50" borderRadius="lg">
                    <HStack justify="space-between" mb={1}>
                      <Text fontWeight="semibold" color="teal.800">{metric.metric}</Text>
                      <Badge colorScheme="teal" variant="subtle">{metric.good}</Badge>
                    </HStack>
                    <Text fontSize="sm" color="teal.600">{metric.meaning}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        );

      case 'safety':
        return (
          <VStack spacing={6} align="stretch">
            <Text mb={4}>{step.content.description}</Text>
            
            <Box>
              <Text fontWeight="semibold" mb={3}>Risk Types & Mitigation:</Text>
              <VStack spacing={3}>
                {step.content.riskTypes.map((risk: any, idx: number) => (
                  <Box key={idx} p={4} bg="yellow.50" borderRadius="lg" w="full" 
                       borderLeft="4px solid" borderColor="yellow.400">
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="semibold" color="yellow.800">{risk.risk}</Text>
                      <Badge colorScheme="yellow" variant="outline">{risk.level}</Badge>
                    </HStack>
                    <Text color="yellow.700" mb={2}>{risk.description}</Text>
                    <Text color="yellow.600" fontSize="sm">
                      <strong>Mitigation:</strong> {risk.mitigation}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={3}>Best Practices:</Text>
              <UnorderedList spacing={1}>
                {step.content.bestPractices.map((practice: string, idx: number) => (
                  <ListItem key={idx} color="gray.700">{practice}</ListItem>
                ))}
              </UnorderedList>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={3}>Recommended Beginner Allocation:</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {Object.entries(step.content.beginnerAllocation).map(([asset, allocation]: [string, string], idx: number) => (
                  <Box key={idx} p={3} bg="green.50" borderRadius="lg">
                    <HStack justify="space-between">
                      <Text color="green.800">{asset}</Text>
                      <Badge colorScheme="green" variant="solid">{allocation}</Badge>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        );

      case 'completion':
        return (
          <VStack spacing={6} align="stretch" textAlign="center">
            <Box>
              <Icon as={Award} boxSize={16} color="purple.500" mb={4} />
              <Heading size="lg" mb={4} color="purple.600">
                Congratulations! 🎉
              </Heading>
              <Text fontSize="lg" mb={6} color="gray.600">
                {step.content.description}
              </Text>
            </Box>

            <Box textAlign="left">
              <Text fontWeight="semibold" mb={3}>Your Next Steps:</Text>
              <VStack spacing={3}>
                {step.content.nextSteps.map((nextStep: any, idx: number) => (
                  <Box key={idx} p={4} bg="purple.50" borderRadius="lg" w="full">
                    <HStack justify="space-between" mb={1}>
                      <Text fontWeight="semibold" color="purple.800">{nextStep.action}</Text>
                      <Badge colorScheme="purple" variant="outline">{nextStep.timeEstimate}</Badge>
                    </HStack>
                    <Text color="purple.700" fontSize="sm">{nextStep.details}</Text>
                  </Box>
                ))}
              </VStack>
            </Box>

            <Box textAlign="left">
              <Text fontWeight="semibold" mb={3}>Available Resources:</Text>
              <UnorderedList spacing={1}>
                {step.content.resources.map((resource: string, idx: number) => (
                  <ListItem key={idx} color="gray.700">{resource}</ListItem>
                ))}
              </UnorderedList>
            </Box>

            <Alert status="warning" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm">{step.content.reminder}</Text>
            </Alert>
          </VStack>
        );

      default:
        return <Text>Content not found</Text>;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Icon as={BookOpen} color="purple.500" boxSize={6} />
              <Text>Alpha Playbook Tutorial</Text>
              <Badge colorScheme="purple" variant="subtle">Beginner Guide</Badge>
            </HStack>
            
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.600">
                  Step {currentStep + 1} of {steps.length}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {Math.round(progress)}% Complete
                </Text>
              </HStack>
              <Progress value={progress} colorScheme="purple" borderRadius="full" />
            </Box>
          </VStack>
        </ModalHeader>
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Step Navigation */}
            <HStack spacing={2} justify="center" flexWrap="wrap">
              {steps.map((step, idx) => (
                <Button
                  key={step.id}
                  size="sm"
                  variant={currentStep === idx ? "solid" : completedSteps.includes(idx) ? "outline" : "ghost"}
                  colorScheme={currentStep === idx ? "purple" : completedSteps.includes(idx) ? "green" : "gray"}
                  onClick={() => handleStepClick(idx)}
                  leftIcon={completedSteps.includes(idx) ? <CheckCircle size={16} /> : undefined}
                >
                  {idx + 1}
                </Button>
              ))}
            </HStack>

            {/* Step Title */}
            <Box textAlign="center">
              <Heading size="xl" color="purple.600">
                {currentStepData.title}
              </Heading>
            </Box>

            {/* Step Content */}
            <Card>
              <CardBody>
                {renderStepContent()}
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              leftIcon={<ChevronLeft size={16} />}
              onClick={handlePrevious}
              isDisabled={currentStep === 0}
              variant="outline"
            >
              Previous
            </Button>
            
            <Button
              rightIcon={currentStep === steps.length - 1 ? <CheckCircle size={16} /> : <ChevronRight size={16} />}
              onClick={handleNext}
              colorScheme="purple"
            >
              {currentStep === steps.length - 1 ? 'Complete Tutorial' : 'Next Step'}
            </Button>

            <Button variant="ghost" onClick={onClose}>
              Skip Tutorial
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OnboardingTutorial;